/**
 * Cliente HTTP centralizado para a API do sapl-map.
 *
 * Mock data pode ser usado apenas durante desenvolvimento. Em produção,
 * falhas da API são propagadas para que a interface não exiba dados falsos.
 *
 * @module services/api
 */

import { THEMATIC_CATEGORIES_ORDER } from "../constants/categories.js";
import {
  canonicalizeBairro,
  getBairroSearchTerms,
  areSameNeighborhood,
} from "../constants/neighborhoods.js";
import { MOCK_INDICACOES, MOCK_STATS } from "./mockData.js";

const globalObj = typeof globalThis !== "undefined" ? globalThis : {};
const proc = globalObj.process;
const env =
  typeof import.meta !== "undefined" && import.meta.env
    ? import.meta.env
    : (proc && proc.env) || {};

const isDev =
  Boolean(env.DEV) ||
  Boolean(proc && proc.env && proc.env.NODE_ENV === "test") ||
  typeof window === "undefined";

const MOCK_DATA_ENABLED =
  isDev && env.VITE_ENABLE_MOCK_DATA !== "false";

function getBaseUrl() {
  const envUrl = env.VITE_API_BASE_URL;
  if (
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1" &&
    envUrl &&
    envUrl.includes("localhost")
  ) {
    return "";
  }
  return envUrl ?? "";
}

const ADMIN_TOKEN_KEY = "sapl-map-admin-token";

export function getAdminToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token) {
  window.localStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken() {
  window.localStorage.removeItem(ADMIN_TOKEN_KEY);
}

/**
 * Wrapper genérico para fetch com tratamento de erros.
 */
async function request(path, options = {}) {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${path}`;
  const token = getAdminToken();

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers["X-Admin-Token"] = token;
  }

  const controller = new AbortController();
  const timeoutMs = 20000;
  const timeoutId = setTimeout(() => {
    controller.abort(new Error("Tempo limite de conexão com o servidor excedido"));
  }, timeoutMs);

  if (options.signal) {
    if (options.signal.aborted) {
      controller.abort(options.signal.reason);
    } else {
      options.signal.addEventListener("abort", () => {
        controller.abort(options.signal.reason);
      });
    }
  }

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorBody = await response.text().catch(() => "Sem corpo na resposta");
      const error = new Error(`API ${response.status} ${response.statusText}: ${errorBody}`);
      error.status = response.status;
      error.body = errorBody;
      throw error;
    }

    if (response.status === 204) return null;
    return response.json();
  } catch (err) {
    clearTimeout(timeoutId);
    throw err;
  }
}

async function requestWithDevelopmentFallback(requestFn, fallbackFn, message) {
  try {
    return await requestFn();
  } catch (error) {
    if (!MOCK_DATA_ENABLED) throw error;

    console.warn(`${message} Usando Mock Data apenas em desenvolvimento.`, error.message);
    return fallbackFn();
  }
}

// ── Helpers de Mock Fallback ──────────────────────────────────────────────────

function normalizeBairro(str) {
  return (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(jardim|jd|pq|parque|res|residencial|chac|chacara|desm|bairro|conj|estancia|gleba|de|da|do|das|dos)\b/gi, " ")
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function matchesBairro(indicacao, targetBairro) {
  if (!targetBairro) return true;
  const { canonical, positive, negative } = getBairroSearchTerms(targetBairro);

  const bairroCandidates = [
    indicacao.bairro,
    ...(indicacao.enderecos_extraidos?.map((e) => e.bairro) || []),
  ].filter(Boolean);

  // 1. Verificação direta de equivalência canônica nos campos estruturados de bairro
  for (const b of bairroCandidates) {
    if (areSameNeighborhood(b, targetBairro) || areSameNeighborhood(b, canonical)) {
      return true;
    }
  }

  // 2. Se a indicação tiver bairro explícito divergente que bata com os negativos (ex: Sacilotto II em busca de Sacilotto I), rejeita
  if (bairroCandidates.length > 0 && negative.length > 0) {
    for (const b of bairroCandidates) {
      const bLower = b.toLowerCase();
      for (const neg of negative) {
        if (bLower.includes(neg.toLowerCase())) {
          return false;
        }
      }
    }
  }

  // 3. Verificação textual em ementa, endereços e pins
  const textCandidates = [
    indicacao.ementa,
    ...(indicacao.enderecos_extraidos?.map((e) => e.raw_text) || []),
    ...(indicacao.enderecos_extraidos?.map((e) => e.street) || []),
    ...(indicacao.geo_pins?.map((p) => p.raw_address || p.normalized_address) || []),
  ].filter(Boolean);

  // Exclusão estrita de menções de outras fases (ex: ementa com "Sacilotto II" em busca de "Sacilotto I")
  if (negative.length > 0) {
    const hasNegative = textCandidates.some((t) => {
      const tLower = t.toLowerCase();
      return negative.some((neg) => tLower.includes(neg.toLowerCase()));
    });
    if (hasNegative) {
      const hasExclusivePositive = textCandidates.some((t) => {
        const tLower = t.toLowerCase();
        return positive
          .filter((p) => /\b(i{1,3}|iv|[1-5])\b/i.test(p))
          .some((pos) => tLower.includes(pos.toLowerCase()));
      });
      if (!hasExclusivePositive) {
        return false;
      }
    }
  }

  for (const t of textCandidates) {
    const tLower = t.toLowerCase();
    for (const pos of positive) {
      if (tLower.includes(pos.toLowerCase())) {
        return true;
      }
    }
  }

  return false;
}

function filterMockIndicacoes(params = {}) {
  let list = [...MOCK_INDICACOES];

  if (params.anos && Array.isArray(params.anos) && params.anos.length > 0) {
    const anosNum = params.anos.map(Number);
    list = list.filter((i) => anosNum.includes(Number(i.ano)));
  }

  if (params.autor) {
    const target = params.autor.trim().toLowerCase();
    list = list.filter((i) =>
      i.autor &&
      i.autor.split(",").some((a) => a.trim().toLowerCase() === target)
    );
  }

  if (params.thematic_category) {
    list = list.filter((i) => i.thematic_category === params.thematic_category);
  }

  if (params.bairro) {
    list = list.filter((i) => matchesBairro(i, params.bairro));
  }

  if (params.search) {
    const q = params.search.toLowerCase().trim();
    list = list.filter((i) =>
      i.ementa.toLowerCase().includes(q) ||
      String(i.numero).includes(q) ||
      `${i.numero}/${i.ano}`.includes(q)
    );
  }

  if (params.aproximado !== undefined && params.aproximado !== null) {
    list = list.filter((i) =>
      i.geo_pins?.some((p) => p.is_approximate === params.aproximado)
    );
  }

  const page = params.page || 1;
  const pageSize = params.pageSize || 20;
  const total = list.length;
  const pages = Math.ceil(total / pageSize) || 1;
  const items = list.slice((page - 1) * pageSize, page * pageSize);

  return { items, total, page, page_size: pageSize, pages };
}

function filterMockPins(params = {}) {
  let indicacoes = [...MOCK_INDICACOES];

  if (params.anos && Array.isArray(params.anos) && params.anos.length > 0) {
    const anosNum = params.anos.map(Number);
    indicacoes = indicacoes.filter((i) => anosNum.includes(Number(i.ano)));
  }

  if (params.autor) {
    const target = params.autor.trim().toLowerCase();
    indicacoes = indicacoes.filter((i) =>
      i.autor &&
      i.autor.split(",").some((a) => a.trim().toLowerCase() === target)
    );
  }

  if (params.thematic_category) {
    indicacoes = indicacoes.filter((i) => i.thematic_category === params.thematic_category);
  }

  if (params.bairro) {
    indicacoes = indicacoes.filter((i) => matchesBairro(i, params.bairro));
  }

  if (params.search) {
    const q = params.search.toLowerCase().trim();
    indicacoes = indicacoes.filter((i) =>
      i.ementa.toLowerCase().includes(q) ||
      String(i.numero).includes(q) ||
      `${i.numero}/${i.ano}`.includes(q)
    );
  }

  let pins = indicacoes.flatMap((i) => {
    return (i.geo_pins || []).map((p) => {
      const matchedEndereco = i.enderecos_extraidos?.find((e) => e.id === p.endereco_id);
      const pinBairro = p.bairro || matchedEndereco?.bairro || i.bairro || i.enderecos_extraidos?.[0]?.bairro || null;
      return {
        ...p,
        bairro: pinBairro,
        indicacao: {
          ...(p.indicacao || {}),
          id: i.id,
          numero: i.numero,
          ano: i.ano,
          ementa: i.ementa,
          autor: i.autor,
          thematic_category: i.thematic_category,
          bairro: pinBairro,
        },
      };
    });
  });

  if (params.aproximado !== undefined && params.aproximado !== null) {
    pins = pins.filter((p) => p.is_approximate === params.aproximado);
  }

  return pins;
}

function computeMockStats(params = {}) {
  let list = [...MOCK_INDICACOES];

  if (params.anos && Array.isArray(params.anos) && params.anos.length > 0) {
    const anosNum = params.anos.map(Number);
    list = list.filter((i) => anosNum.includes(Number(i.ano)));
  }

  if (params.autor) {
    const target = params.autor.trim().toLowerCase();
    list = list.filter((i) =>
      i.autor &&
      i.autor.split(",").some((a) => a.trim().toLowerCase() === target)
    );
  }

  if (params.thematic_category) {
    list = list.filter((i) => i.thematic_category === params.thematic_category);
  }

  if (params.bairro) {
    list = list.filter((i) => matchesBairro(i, params.bairro));
  }

  if (params.search) {
    const q = params.search.toLowerCase().trim();
    list = list.filter((i) =>
      i.ementa.toLowerCase().includes(q) ||
      String(i.numero).includes(q) ||
      `${i.numero}/${i.ano}`.includes(q)
    );
  }

  if (params.aproximado !== undefined && params.aproximado !== null) {
    list = list.filter((i) =>
      i.geo_pins?.some((p) => p.is_approximate === params.aproximado)
    );
  }

  const allPins = list.flatMap((i) => i.geo_pins || []);
  const total_pins = allPins.length;
  const pins_aproximados = allPins.filter((p) => p.is_approximate).length;

  const autores = MOCK_STATS.autores || [];
  const anos = MOCK_STATS.anos || [];

  const globalAuthorsSet = new Set(autores.map((a) => a.trim().toLowerCase()).filter(Boolean));
  const globalMandatosSet = new Set();
  const globalBairrosAtendidosSet = new Set();

  MOCK_INDICACOES.forEach((ind) => {
    const legNum = Number(ind.ano) >= 2025 ? 19 : 18;
    if (ind.autor) {
      ind.autor.split(",").forEach((part) => {
        const clean = part.trim();
        if (clean && clean.toLowerCase() !== "n/a") {
          globalAuthorsSet.add(clean.toLowerCase());
          globalMandatosSet.add(`${clean.toLowerCase()}::${legNum}`);
        }
      });
    }
    if (ind.bairro) {
      const c = canonicalizeBairro(ind.bairro);
      if (c && c !== "Não informado") globalBairrosAtendidosSet.add(c.toLowerCase());
    }
    (ind.enderecos_extraidos || []).forEach((e) => {
      if (e.bairro) {
        const c = canonicalizeBairro(e.bairro);
        if (c && c !== "Não informado") globalBairrosAtendidosSet.add(c.toLowerCase());
      }
    });
    (ind.geo_pins || []).forEach((p) => {
      if (p.bairro) {
        const c = canonicalizeBairro(p.bairro);
        if (c && c !== "Não informado") globalBairrosAtendidosSet.add(c.toLowerCase());
      }
    });
  });

  const global_total_indicacoes = MOCK_INDICACOES.length;
  const global_total_autores = Math.max(autores.length, globalAuthorsSet.size);
  const global_total_mandatos = Math.max(globalMandatosSet.size, global_total_autores);
  const globalLegislaturasSet = new Set(
    anos.map((y) => (y >= 2025 ? 19 : y >= 2021 ? 18 : y >= 2017 ? 17 : 16))
  );
  const global_total_legislaturas = globalLegislaturasSet.size || 2;
  const global_total_bairros_oficiais = 132;
  const global_bairros_atendidos = globalBairrosAtendidosSet.size;

  const bairrosMap = new Map();
  (MOCK_STATS.bairros || []).forEach((b) => {
    const c = canonicalizeBairro(b);
    if (c) bairrosMap.set(c.toLowerCase(), c);
  });
  list.forEach((i) => {
    if (i.bairro) {
      const c = canonicalizeBairro(i.bairro);
      if (c) bairrosMap.set(c.toLowerCase(), c);
    }
    (i.enderecos_extraidos || []).forEach((e) => {
      if (e.bairro) {
        const c = canonicalizeBairro(e.bairro);
        if (c) bairrosMap.set(c.toLowerCase(), c);
      }
    });
  });
  const bairros = Array.from(bairrosMap.values()).sort((a, b) => a.localeCompare(b, "pt-BR"));

  const thematicCounts = {};
  THEMATIC_CATEGORIES_ORDER.forEach((cat) => {
    thematicCounts[cat] = 0;
  });
  list.forEach((i) => {
    const c = i.thematic_category || "OUTROS";
    thematicCounts[c] = (thematicCounts[c] || 0) + 1;
  });
  const demandas_por_thematic_category = THEMATIC_CATEGORIES_ORDER.map((category) => ({
    category,
    count: thematicCounts[category] || 0,
  }));

  const yearCounts = {};
  anos.forEach((y) => {
    yearCounts[y] = 0;
  });
  list.forEach((i) => {
    if (i.ano) {
      yearCounts[i.ano] = (yearCounts[i.ano] || 0) + 1;
    }
  });
  const demandas_por_ano = anos.map((ano) => ({
    ano,
    count: yearCounts[ano] || 0,
  }));

  const authorCounts = {};
  list.forEach((i) => {
    if (i.autor) {
      i.autor.split(",").forEach((a) => {
        const clean = a.trim();
        if (clean) {
          authorCounts[clean] = (authorCounts[clean] || 0) + 1;
        }
      });
    }
  });
  const demandas_por_autor = autores.map((autor) => ({
    autor,
    count: authorCounts[autor] || 0,
  })).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.autor.localeCompare(b.autor, "pt-BR");
  });

  const bairroCounts = {};
  const bairroCatCounts = {};

  list.forEach((i) => {
    const bSet = new Set();
    const cat = i.thematic_category || "OUTROS";

    if (i.bairro) {
      const c = canonicalizeBairro(i.bairro);
      if (c && c !== "Não informado") bSet.add(c);
    }
    (i.enderecos_extraidos || []).forEach((e) => {
      if (e.bairro) {
        const c = canonicalizeBairro(e.bairro);
        if (c && c !== "Não informado") bSet.add(c);
      }
    });
    (i.geo_pins || []).forEach((p) => {
      if (p.bairro) {
        const c = canonicalizeBairro(p.bairro);
        if (c && c !== "Não informado") bSet.add(c);
      }
    });

    bSet.forEach((b) => {
      bairroCounts[b] = (bairroCounts[b] || 0) + 1;
      if (!bairroCatCounts[b]) bairroCatCounts[b] = {};
      bairroCatCounts[b][cat] = (bairroCatCounts[b][cat] || 0) + 1;
    });
  });

  const demandas_por_bairro = Object.entries(bairroCounts)
    .map(([bairro, count]) => {
      const catsMap = bairroCatCounts[bairro] || {};
      const sortedCats = Object.entries(catsMap).sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1];
        return a[0].localeCompare(b[0], "pt-BR");
      });
      const domCat = sortedCats[0] ? sortedCats[0][0] : null;
      const domCnt = sortedCats[0] ? sortedCats[0][1] : 0;

      return {
        bairro,
        count,
        dominant_category: domCat,
        dominant_category_count: domCnt,
      };
    })
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.bairro.localeCompare(b.bairro, "pt-BR");
    });

  return {
    total_indicacoes: list.length,
    global_total_indicacoes,
    global_total_autores,
    global_total_mandatos,
    global_total_legislaturas,
    global_total_bairros_oficiais,
    global_bairros_atendidos,
    total_pins,
    pins_aproximados,
    autores,
    anos,
    bairros,
    demandas_por_thematic_category,
    demandas_por_ano,
    demandas_por_autor,
    demandas_por_bairro,
  };
}

// ── Indicações ──────────────────────────────────────────────────────────────

export async function fetchIndicacoes(params = {}) {
  const query = new URLSearchParams();

  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("page_size", String(params.pageSize));
  if (params.anos && Array.isArray(params.anos) && params.anos.length > 0) {
    query.set("anos", params.anos.join(","));
  }
  if (params.autor) query.set("autor", params.autor);
  if (params.bairro_id !== undefined && params.bairro_id !== null) {
    query.set("bairro_id", String(params.bairro_id));
  }
  if (params.bairro) query.set("bairro", params.bairro);
  if (params.thematic_category) query.set("thematic_category", params.thematic_category);
  if (params.search) query.set("search", params.search);
  if (params.aproximado !== undefined && params.aproximado !== null) {
    query.set("aproximado", String(params.aproximado));
  }

  const qs = query.toString();
  return requestWithDevelopmentFallback(
    () => request(`/api/indicacoes${qs ? `?${qs}` : ""}`),
    () => filterMockIndicacoes(params),
    "Backend indisponível para indicações."
  );
}

export async function fetchIndicacaoById(id) {
  return requestWithDevelopmentFallback(
    () => request(`/api/indicacoes/${id}`),
    () => MOCK_INDICACOES.find((i) => i.id === Number(id)) || null,
    `Backend indisponível para indicação ${id}.`
  );
}

// ── Geo Pins ────────────────────────────────────────────────────────────────

export async function fetchGeoPins(params = {}) {
  const query = new URLSearchParams();

  if (params.status) query.set("status", params.status);
  if (params.anos && Array.isArray(params.anos) && params.anos.length > 0) {
    query.set("anos", params.anos.join(","));
  }
  if (params.autor) query.set("autor", params.autor);
  if (params.bairro_id !== undefined && params.bairro_id !== null) {
    query.set("bairro_id", String(params.bairro_id));
  }
  if (params.bairro) query.set("bairro", params.bairro);
  if (params.thematic_category) query.set("thematic_category", params.thematic_category);
  if (params.search) query.set("search", params.search);
  if (params.aproximado !== undefined && params.aproximado !== null) {
    query.set("aproximado", String(params.aproximado));
  }

  const qs = query.toString();
  return requestWithDevelopmentFallback(
    () => request(`/api/geo-pins${qs ? `?${qs}` : ""}`),
    () => filterMockPins(params),
    "Backend indisponível para geo-pins."
  );
}

// ── Estatísticas ────────────────────────────────────────────────────────────

export async function fetchStats(params = {}) {
  const query = new URLSearchParams();

  if (params.anos && Array.isArray(params.anos) && params.anos.length > 0) {
    query.set("anos", params.anos.join(","));
  }
  if (params.autor) query.set("autor", params.autor);
  if (params.bairro_id !== undefined && params.bairro_id !== null) {
    query.set("bairro_id", String(params.bairro_id));
  }
  if (params.bairro) query.set("bairro", params.bairro);
  if (params.thematic_category) query.set("thematic_category", params.thematic_category);
  if (params.search) query.set("search", params.search);
  if (params.aproximado !== undefined && params.aproximado !== null) {
    query.set("aproximado", String(params.aproximado));
  }

  const qs = query.toString();
  return requestWithDevelopmentFallback(
    () => request(`/api/stats${qs ? `?${qs}` : ""}`),
    () => computeMockStats(params),
    "Backend indisponível para estatísticas."
  );
}

export async function fetchBairros() {
  return requestWithDevelopmentFallback(
    () => request("/api/bairros"),
    () => [],
    "Backend indisponível para catálogo de bairros."
  );
}

// ── Admin Fallbacks ─────────────────────────────────────────────────────────

export async function fetchReviewAddresses(params = {}) {
  const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : "";
  return request(`/api/admin/revisao/enderecos${qs}`);
}

export async function fetchReviewIndicacoes() {
  return request("/api/admin/revisao/indicacoes");
}

export async function updateManualAddress(id, data) {
  return request(`/api/admin/enderecos/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function createManualAddress(data) {
  const { indicacao_id: indicacaoId, street, number, bairro } = data;
  return request(`/api/admin/indicacoes/${indicacaoId}/enderecos`, {
    method: "POST",
    body: JSON.stringify({
      street,
      number: number || null,
      bairro: bairro || null,
    }),
  });
}

export async function deleteAddress(id) {
  return request(`/api/admin/enderecos/${id}`, {
    method: "DELETE",
  });
}

export async function triggerSync() {
  return request("/api/admin/sync", { method: "POST" });
}

export async function triggerReprocess(resetNonFixed = true, geocode = true) {
  return request(`/api/admin/reprocess?reset_non_fixed=${resetNonFixed}&geocode=${geocode}`, { method: "POST" });
}

export async function triggerReclassifyThemes() {
  return request("/api/admin/reprocess/themes", { method: "POST" });
}

export async function triggerExtractAddresses(resetNonFixed = true) {
  return request(`/api/admin/reprocess/extract?reset_non_fixed=${resetNonFixed}`, { method: "POST" });
}

export async function triggerResolvePostGIS() {
  return request("/api/admin/reprocess/resolve", { method: "POST" });
}

export async function triggerGeocodePending() {
  return request("/api/admin/geocode-pending", { method: "POST" });
}

export async function fetchAuditoria(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("page_size", String(params.pageSize));
  if (params.status) query.set("status", params.status);
  if (params.search) query.set("search", params.search);
  if (params.sortBy) query.set("sort_by", params.sortBy);
  if (params.sortDir) query.set("sort_dir", params.sortDir);
  const qs = query.toString();
  return request(`/api/admin/auditoria${qs ? `?${qs}` : ""}`);
}

export function getAuditoriaCsvUrl() {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/api/admin/auditoria-export.csv`;
}

export async function triggerReleaseLocks() {
  return request("/api/admin/locks/release", { method: "POST" });
}

export async function saveAddressPin(enderecoId, payload) {
  return request(`/api/admin/enderecos/${enderecoId}/pin`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function createIndicacaoPin(indicacaoId, payload) {
  return request(`/api/admin/indicacoes/${indicacaoId}/pin`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function unfixAddress(enderecoId) {
  return request(`/api/admin/enderecos/${enderecoId}/unfix`, {
    method: "POST",
  });
}

export async function reprocessIndicacao(indicacaoId) {
  return request(`/api/admin/indicacoes/${indicacaoId}/reprocess`, {
    method: "POST",
  });
}
export async function triggerBatchReprocess(selection, action = "full") {
  return request("/api/admin/reprocess/batch", {
    method: "POST",
    body: JSON.stringify({ ...selection, action }),
  });
}

export async function markBatchReview(selection) {
  return request("/api/admin/indicacoes/review-batch", {
    method: "POST",
    body: JSON.stringify(selection),
  });
}

export async function countAuditoriaMatches(filter) {
  return request("/api/admin/auditoria/count", {
    method: "POST",
    body: JSON.stringify(filter),
  });
}
export async function updateIndicacaoTema(indicacaoId, category) {
  return request(`/api/admin/indicacoes/${indicacaoId}/tema`, {
    method: "PATCH",
    body: JSON.stringify({ category }),
  });
}

export async function updateBatchTema(selection, category) {
  return request("/api/admin/indicacoes/tema-batch", {
    method: "POST",
    body: JSON.stringify({ ...selection, category }),
  });
}
